import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './DashboardPage.css';
import { supabase } from './supabaseClient';

const DashboardPage = () => {
  const navigate = useNavigate();

  const [userName, setUserName] = React.useState('');
  const [upcomingFlights, setUpcomingFlights] = React.useState([]);
  const [pastTrips, setPastTrips] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Fetch user bookings from Supabase
  React.useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        
        // Check if user is logged in
        const isSignedIn = localStorage.getItem('isSignedIn');
        const userData = localStorage.getItem('user');

        if (!isSignedIn || !userData) {
          navigate('/login');
          return;
        }

        const user = JSON.parse(userData);
        setUserName(user.name || 'User');

        // Fetch bookings from flight_bookings table (or bookings table if flight_bookings doesn't exist)
        const today = new Date().toISOString().split('T')[0];
        
        // Try flight_bookings first, fallback to bookings
        let { data: bookings, error: bookingsError } = await supabase
          .from('flight_bookings')
          .select('*')
          .eq('user_id', user.id)
          .order('outbound_departure_date', { ascending: true });

        // If flight_bookings doesn't exist, try bookings table
        if (bookingsError && bookingsError.code === '42P01') {
          const { data: altBookings, error: altError } = await supabase
            .from('bookings')
            .select(`
              *,
              flights (
                *,
                routes (
                  destinations_from:destinations!routes_from_airport_id_fkey (*),
                  destinations_to:destinations!routes_to_airport_id_fkey (*)
                )
              )
            `)
            .eq('user_id', user.id)
            .order('booking_date', { ascending: false });

          if (altError) throw altError;
          bookings = altBookings;
        } else if (bookingsError) {
          throw bookingsError;
        }

        if (!bookings) {
          setUpcomingFlights([]);
          setPastTrips([]);
          setLoading(false);
          return;
        }

        // Format bookings for display
        const formattedBookings = bookings.map(booking => {
          // Handle both flight_bookings and bookings table structures
          const isFlightBookings = booking.outbound_departure_date !== undefined;
          
          if (isFlightBookings) {
            // flight_bookings table structure
            return {
              id: booking.id,
              bookingReference: booking.booking_reference || booking.booking_id || `SF${booking.id.substring(0, 8).toUpperCase()}`,
              airline: booking.outbound_airline || 'Airline',
              airlineLogo: '✈️',
              departureAirport: booking.outbound_departure_airport || '',
              departureCity: booking.outbound_departure_city || '',
              departureDate: booking.outbound_departure_date ? new Date(booking.outbound_departure_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
              departureTime: booking.outbound_departure_time || '',
              arrivalAirport: booking.outbound_arrival_airport || '',
              arrivalCity: booking.outbound_arrival_city || '',
              arrivalDate: booking.outbound_arrival_date ? new Date(booking.outbound_arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
              arrivalTime: booking.outbound_arrival_time || '',
              duration: booking.outbound_duration ? `${Math.floor(booking.outbound_duration / 60)}h ${booking.outbound_duration % 60}m` : '',
              travelClass: booking.cabin_class || 'economy',
              price: `₦${parseFloat(booking.total_amount || 0).toLocaleString()}`,
              status: booking.booking_status || 'pending',
              date: booking.outbound_departure_date || booking.booking_date,
              route: `${booking.outbound_departure_city || ''} → ${booking.outbound_arrival_city || ''}`
            };
          } else {
            // bookings table structure (with flights relation)
            const flight = booking.flights;
            const route = flight?.routes;
            const fromDest = route?.destinations_from;
            const toDest = route?.destinations_to;
            
            return {
              id: booking.id,
              bookingReference: booking.booking_id || `SF${booking.id.substring(0, 8).toUpperCase()}`,
              airline: flight?.airline_name || 'Airline',
              airlineLogo: '✈️',
              departureAirport: fromDest?.airport_code || '',
              departureCity: fromDest?.city_name || '',
              departureDate: flight?.departure_date ? new Date(flight.departure_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
              departureTime: flight?.departure_time || '',
              arrivalAirport: toDest?.airport_code || '',
              arrivalCity: toDest?.city_name || '',
              arrivalDate: flight?.arrival_date ? new Date(flight.arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
              arrivalTime: flight?.arrival_time || '',
              duration: flight?.duration ? `${Math.floor(flight.duration / 60)}h ${flight.duration % 60}m` : '',
              travelClass: booking.cabin_class || 'economy',
              price: `₦${parseFloat(booking.total_amount || 0).toLocaleString()}`,
              status: booking.booking_status || 'pending',
              date: flight?.departure_date || booking.booking_date,
              route: `${fromDest?.city_name || ''} → ${toDest?.city_name || ''}`
            };
          }
        });

        // Separate into upcoming and past trips
        const now = new Date();
        const upcoming = formattedBookings.filter(booking => {
          if (!booking.date) return false;
          const bookingDate = new Date(booking.date);
          return bookingDate >= now && booking.status !== 'cancelled';
        });

        const past = formattedBookings.filter(booking => {
          if (!booking.date) return false;
          const bookingDate = new Date(booking.date);
          return bookingDate < now || booking.status === 'completed';
        });

        setUpcomingFlights(upcoming);
        setPastTrips(past);

      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load bookings');
        setUpcomingFlights([]);
        setPastTrips([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [navigate]);

  const handleBookFlight = () => {
    navigate('/');
  };

  const handleViewDetails = (flightId) => {
    // Find the flight data
    const flight = upcomingFlights.find(f => f.id === flightId);
    if (flight) {
      // Navigate to trip details page with flight data
      navigate(`/trip/${flightId}`, { 
        state: { 
          trip: {
            ...flight,
            flightNumber: `BA${flightId}23`,
            aircraftType: flight.aircraftType || 'Boeing 737',
            terminal: 'Terminal 1',
            gate: 'Gate A12',
            boardingTime: flight.departureTime ? 
              `${String(parseInt(flight.departureTime.split(':')[0]) - 1).padStart(2, '0')}:${flight.departureTime.split(':')[1]}` : 
              '13:50',
            passengers: [
              {
                name: 'John Doe',
                type: 'Adult',
                seat: '12A',
                ticketRef: `TKT${flight.bookingReference}`,
                nationality: 'Nigeria',
                gender: 'Male'
              }
            ],
            baggage: {
              carryOn: '1 piece (7kg)',
              checked: flight.travelClass === 'Business' ? '2 pieces (23kg each)' : '1 piece (23kg)'
            },
            meals: 'Available',
            price: {
              baseFare: '₦750,000',
              taxes: '₦100,000',
              total: flight.price || '₦850,000'
            },
            paymentMethod: 'Paystack'
          }
        } 
      });
    }
  };

  const handleViewReceipt = (tripId) => {
    // Find the trip data
    const trip = pastTrips.find(t => t.id === tripId);
    if (trip) {
      // Navigate to trip details page with trip data
      navigate(`/trip/${tripId}`, { 
        state: { 
          trip: {
            ...trip,
            departureAirport: trip.route.split(' → ')[0].split(' ')[0],
            arrivalAirport: trip.route.split(' → ')[1].split(' ')[0],
            departureCity: trip.route.split(' → ')[0].split(' ')[0],
            arrivalCity: trip.route.split(' → ')[1].split(' ')[0],
            departureTime: '10:00',
            arrivalTime: '12:00',
            departureDate: trip.date,
            arrivalDate: trip.date,
            duration: '2h 0m',
            stops: 'Nonstop',
            travelClass: 'Economy',
            status: 'Completed',
            flightNumber: `EM${tripId}45`,
            aircraftType: 'Boeing 737',
            terminal: 'Terminal 1',
            gate: 'Gate B5',
            boardingTime: '09:30',
            passengers: [
              {
                name: 'John Doe',
                type: 'Adult',
                seat: null,
                ticketRef: `TKT${trip.bookingReference}`,
                nationality: 'Nigeria',
                gender: 'Male'
              }
            ],
            baggage: {
              carryOn: '1 piece (7kg)',
              checked: '1 piece (23kg)'
            },
            meals: 'Available',
            price: {
              baseFare: trip.price === '₦720,000' ? '₦650,000' : '₦35,000',
              taxes: trip.price === '₦720,000' ? '₦70,000' : '₦3,000',
              total: trip.price
            },
            paymentMethod: 'Paystack'
          }
        } 
      });
    }
  };

  return (
    <div className="dashboard-page">
      <Header />
      
      {/* Welcome Hero Panel */}
      <section className="dashboard-hero-section">
        <div className="page-container">
          <div className="welcome-hero-card">
            <div className="welcome-content">
              <div className="welcome-icon">✈️</div>
              <div className="welcome-text">
                <h1 className="welcome-title">Welcome Back, {userName}</h1>
                <p className="welcome-subtitle">
                  Here are your flights and upcoming plans.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Flights Section */}
      <section className="upcoming-flights-section">
        <div className="page-container">
          <h2 className="section-title">Upcoming Flights</h2>
          
          {upcomingFlights.length > 0 ? (
            <div className="flights-grid">
              {upcomingFlights.map(flight => (
                <div key={flight.id} className="flight-card">
                  <div className="flight-card-header">
                    <div className="flight-airline-info">
                      <span className="flight-logo">{flight.airlineLogo}</span>
                      <div>
                        <h3 className="flight-airline-name">{flight.airline}</h3>
                        <span className="flight-reference">Ref: {flight.bookingReference}</span>
                      </div>
                    </div>
                    <span className={`status-badge ${flight.status.toLowerCase()}`}>
                      {flight.status}
                    </span>
                  </div>

                  <div className="flight-route-info">
                    <div className="route-segment">
                      <span className="route-time">{flight.departureTime}</span>
                      <span className="route-date">{flight.departureDate}</span>
                      <span className="route-airport">{flight.departureAirport}</span>
                      <span className="route-city">{flight.departureCity}</span>
                    </div>

                    <div className="route-connector">
                      <span className="connector-duration">{flight.duration}</span>
                      <div className="connector-line">
                        <span className="connector-icon">✈️</span>
                      </div>
                      <span className="connector-class">{flight.travelClass}</span>
                    </div>

                    <div className="route-segment">
                      <span className="route-time">{flight.arrivalTime}</span>
                      <span className="route-date">{flight.arrivalDate}</span>
                      <span className="route-airport">{flight.arrivalAirport}</span>
                      <span className="route-city">{flight.arrivalCity}</span>
                    </div>
                  </div>

                  <button 
                    className="view-details-button"
                    onClick={() => handleViewDetails(flight.id)}
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">✈️</div>
              <h3 className="empty-title">No Upcoming Flights</h3>
              <p className="empty-text">Start planning your next journey</p>
              <button className="empty-cta-button" onClick={handleBookFlight}>
                Book a Flight
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Past Trips Section */}
      <section className="past-trips-section">
        <div className="page-container">
          <h2 className="section-title">Past Trips</h2>
          
          {pastTrips.length > 0 ? (
            <div className="trips-grid">
              {pastTrips.map(trip => (
                <div key={trip.id} className="trip-card">
                  <div className="trip-header">
                    <div className="trip-airline">
                      <span className="trip-logo">{trip.airlineLogo}</span>
                      <span className="trip-airline-name">{trip.airline}</span>
                    </div>
                    <span className="trip-reference">Ref: {trip.bookingReference}</span>
                  </div>

                  <div className="trip-details">
                    <div className="trip-route">{trip.route}</div>
                    <div className="trip-date">{trip.date}</div>
                    <div className="trip-price">{trip.price}</div>
                  </div>

                  <button 
                    className="view-receipt-button"
                    onClick={() => handleViewReceipt(trip.id)}
                  >
                    View Receipt
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state small">
              <p className="empty-text">No past trips yet</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DashboardPage;

